// Confirmation page — C3 redesign (prix + CTA sticky).
// Layout: violet Hero → scrollable card column (Trajet, Véhicule, Affectation
// selects, mention légale) → sticky footer with price block + accept CTA.
// Tokens/icons mirror the V2d driver tracking screen pattern but are inlined
// here to avoid cross-folder imports; the confirmation screen is a
// self-contained public page and Bootstrap/kt- chrome has been removed.

import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { components as selectComponents } from "react-select";
import axios from "axios";
import { useParams } from "react-router-dom";
import moment from "moment-timezone";
import styled, { css } from "styled-components";

import { apiRoot } from "../../index";

// ────────────────────────────────────────────────────────────────
// Design tokens (local — matches the V2d track screen palette)
// ────────────────────────────────────────────────────────────────

const tokens = {
  colors: {
    violet: "#8B5CF6",
    violetDeep: "#6D28D9",
    page: "#EEECE6",
    surface: "#FFFFFF",
    border: "rgba(20,14,30,0.08)",
    text: "#1C1B22",
    muted: "#6B6A73",
    subtle: "#9A99A3",
    violetTint: "rgba(139,92,246,0.1)",
    ctaShadow: "0 6px 18px rgba(139,92,246,0.4)",
    ctaDisabled: "rgba(139,92,246,0.35)",
    footerBg: "rgba(255,255,255,0.97)",
  },
  radii: { sm: 8, md: 10, lg: 12, xl: 14, hero: 22 },
  typo: { family: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif' },
};

// ────────────────────────────────────────────────────────────────
// Icons — inline SVG (same pattern as driverApp/track)
// ────────────────────────────────────────────────────────────────

const ICON_PATHS = {
  car: "M5 17h14v-3l-2-5H7L5 14v3zM7.5 17a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM16.5 17a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z",
  building: "M3 21h18M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16M9 7h.01M9 11h.01M9 15h.01M13 7h.01M13 11h.01M13 15h.01",
  user: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  chevronDown: "M6 9l6 6 6-6",
  check: "M5 12.5l5 5L20 7",
  calendar: "M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z",
};

function Icon({ path, size = 18, sw = 2, style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

// ────────────────────────────────────────────────────────────────
// Layout primitives
// ────────────────────────────────────────────────────────────────

const PageBg = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background: ${tokens.colors.page};
  color: ${tokens.colors.text};
  font-family: ${tokens.typo.family};
  display: flex;
  flex-direction: column;
`;

const Column = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Hero = styled.div`
  background: linear-gradient(180deg, ${tokens.colors.violetDeep} 0%, ${tokens.colors.violet} 100%);
  color: #fff;
  border-radius: 0 0 ${tokens.radii.hero}px ${tokens.radii.hero}px;
  padding: 16px 22px 22px;
  text-align: center;
`;

const HeroMission = styled.div`
  font-size: 13px;
  font-weight: 500;
  opacity: 0.8;
  margin-bottom: 14px;
`;

const HeroLabel = styled.div`
  font-size: 14px;
  opacity: 0.85;
`;

const HeroTime = styled.div`
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -1.5px;
  line-height: 1;
  margin-top: 2px;
`;

const HeroDate = styled.div`
  font-size: 13px;
  opacity: 0.7;
  margin-top: 4px;
`;

const Scroll = styled.div`
  flex: 1;
  padding: 14px 14px 170px;
`;

const CardsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Card = styled.div`
  background: ${tokens.colors.surface};
  border: 1px solid ${tokens.colors.border};
  border-radius: ${tokens.radii.xl}px;
  padding: 12px 14px;
`;

// Trajet timeline
const TripRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
`;

const TimelineCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 2px;
`;

const TimelineDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid ${tokens.colors.violet};
`;

const TimelineLine = styled.div`
  flex: 1;
  width: 2px;
  min-height: 16px;
  margin: 4px 0;
  background-image: repeating-linear-gradient(
    to bottom,
    ${tokens.colors.violet} 0 4px,
    transparent 4px 8px
  );
`;

const TimelineDiamond = styled.div`
  width: 10px;
  height: 10px;
  background: ${tokens.colors.violet};
  transform: rotate(45deg);
`;

const TripAddresses = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: ${tokens.colors.text};
  line-height: 1.35;
`;

// Véhicule card
const VehicleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconBadge = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${tokens.radii.md}px;
  background: ${tokens.colors.violetTint};
  color: ${tokens.colors.violetDeep};
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;

const VehicleLabel = styled.div`
  font-size: 11px;
  color: ${tokens.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const VehicleValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${tokens.colors.text};
  margin-top: 2px;
`;

// Section label
const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${tokens.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 6px 4px 0;
`;

// Select row with small leading icon badge
const SelectGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectFieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${tokens.colors.text};
  margin-bottom: 6px;
  padding-left: 2px;
`;

const SelectLeadingIcon = styled.div`
  width: 26px;
  height: 26px;
  border-radius: ${tokens.radii.sm}px;
  background: ${tokens.colors.violetTint};
  color: ${tokens.colors.violetDeep};
  display: grid;
  place-items: center;
  margin-left: 12px;
  flex-shrink: 0;
`;

// Mention légale
const LegalMention = styled.p`
  font-size: 11px;
  color: ${tokens.colors.muted};
  line-height: 1.5;
  padding: 4px 6px;
  text-align: center;
  margin: 0;
`;

// Sticky footer
const StickyFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${tokens.colors.footerBg};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-top: 1px solid ${tokens.colors.border};
  padding: 12px 16px calc(10px + env(safe-area-inset-bottom, 34px));
  z-index: 50;
`;

const FooterRow = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const PriceLabel = styled.div`
  font-size: 11px;
  color: ${tokens.colors.muted};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const PriceValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: ${tokens.colors.text};
  margin-top: 2px;
`;

const AcceptButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  border-radius: ${tokens.radii.xl}px;
  border: 0;
  background: ${(p) => (p.$disabled ? tokens.colors.ctaDisabled : tokens.colors.violet)};
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  box-shadow: ${(p) => (p.$disabled ? "none" : tokens.colors.ctaShadow)};
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  transition: transform 0.12s ease;
  &:active {
    transform: ${(p) => (p.$disabled ? "none" : "scale(0.98)")};
  }
  flex-shrink: 0;
  ${(p) => p.$fullWidth && `flex: 1; justify-content: center;`}
`;

// Centered message for loading / success / error states
const CenteredState = styled.div`
  flex: 1;
  display: grid;
  place-items: center;
  padding: 40px 24px;
  text-align: center;
  color: ${tokens.colors.text};
  font-family: ${tokens.typo.family};
`;

// react-select style overrides to match the design.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 48,
    background: tokens.colors.surface,
    border: `1px solid ${tokens.colors.border}`,
    borderRadius: tokens.radii.lg,
    boxShadow: state.isFocused ? `0 0 0 3px ${tokens.colors.violetTint}` : "none",
    paddingLeft: 0,
    paddingRight: 4,
    fontFamily: tokens.typo.family,
    fontSize: 14,
    cursor: "pointer",
    "&:hover": { borderColor: tokens.colors.border },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 10px" }),
  placeholder: (base) => ({ ...base, color: tokens.colors.subtle, fontSize: 14 }),
  singleValue: (base) => ({ ...base, color: tokens.colors.text, fontSize: 14 }),
  // Force 16px on iOS so focusing the input does not trigger the viewport
  // auto-zoom (any font-size < 16px causes that zoom-in on iOS Safari).
  input: (base) => ({
    ...base,
    fontSize: 16,
    "@media (min-width: 720px)": { fontSize: 14 },
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: tokens.colors.muted, padding: 8 }),
  menu: (base) => ({
    ...base,
    backgroundColor: tokens.colors.surface,
    borderRadius: tokens.radii.lg,
    border: `1px solid ${tokens.colors.border}`,
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(20,14,30,0.08)",
    zIndex: 20,
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: tokens.colors.surface,
    padding: 0,
  }),
  option: (base, state) => ({
    ...base,
    // Opaque backgrounds only — violetTint is 10% alpha so baking it onto
    // white yields #F4EFFD; use that directly to avoid any see-through of
    // elements below (e.g. the Chauffeur select icon).
    backgroundColor: state.isSelected
      ? tokens.colors.violet
      : state.isFocused
      ? "#F4EFFD"
      : tokens.colors.surface,
    color: state.isSelected ? "#fff" : tokens.colors.text,
    fontSize: 14,
    cursor: "pointer",
  }),
};

const DropdownIndicator = (props) => (
  <selectComponents.DropdownIndicator {...props}>
    <Icon path={ICON_PATHS.chevronDown} size={16} sw={2} />
  </selectComponents.DropdownIndicator>
);

// Leading icon rendered INSIDE react-select's Control so it anchors to the
// control (not the wrapper) and never overlaps the opened menu.
function makeControlWithIcon(iconPath) {
  return function ControlWithIcon({ children, ...props }) {
    return (
      <selectComponents.Control {...props}>
        <SelectLeadingIcon>
          <Icon path={iconPath} size={14} sw={2} />
        </SelectLeadingIcon>
        {children}
      </selectComponents.Control>
    );
  };
}

const CompanyControl = makeControlWithIcon(ICON_PATHS.building);
const DriverControl = makeControlWithIcon(ICON_PATHS.user);

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────

// pickup_date from the API is already French-formatted, e.g.
// "jeudi 23 avr. 2026 à 11:00". Split to extract time for the hero.
function splitPickupDate(raw) {
  if (!raw || typeof raw !== "string") return { date: "", time: "" };
  const m = raw.match(/^(.+?)\s+à\s+(\d{1,2}:\d{2})$/);
  if (m) return { date: m[1], time: m[2] };
  return { date: raw, time: "" };
}

const euroFormatter = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatPrice(value) {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return euroFormatter.format(n);
}

// ────────────────────────────────────────────────────────────────
// Screen
// ────────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────
// Après acceptation : le bon de mission s'affiche sur la page (le chauffeur
// gagnant seulement — le jeton de suivi n'est remis qu'à son navigateur, et
// gardé en local pour qu'il retrouve sa course en rouvrant le lien).
// ────────────────────────────────────────────────────────────────

const storageKey = (uid, bookingId) => `zeplan_conf_${uid}_${bookingId}`;

function readStoredToken(uid, bookingId) {
  try {
    return window.localStorage.getItem(storageKey(uid, bookingId)) || "";
  } catch (e) {
    return "";
  }
}

function storeToken(uid, bookingId, token) {
  try {
    if (token) window.localStorage.setItem(storageKey(uid, bookingId), token);
  } catch (e) {
    /* stockage indisponible (navigation privée) : la page reste utilisable */
  }
}

function pickupMoment(booking) {
  if (!booking || !booking.date || !booking.time) return null;
  const m = moment.tz(
    moment(booking.date * 1000).tz("Europe/Paris").format("YYYY-MM-DD") +
      " " +
      booking.time,
    "Europe/Paris"
  );
  return m.isValid() ? m : null;
}

function googleCalendarUrl(ev) {
  const fmt = (ms) => moment.utc(ms).format("YYYYMMDD[T]HHmmss[Z]");
  const q = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.title,
    dates: `${fmt(ev.start)}/${fmt(ev.end)}`,
    details: ev.description,
    location: ev.location,
  });
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
}

const actionStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 16px;
  border-radius: ${tokens.radii.xl}px;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid ${tokens.colors.border};
  background: ${(p) => (p.$primary ? tokens.colors.violet : tokens.colors.surface)};
  color: ${(p) => (p.$primary ? "#fff" : tokens.colors.text)};
  box-shadow: ${(p) => (p.$primary ? tokens.colors.ctaShadow : "none")};
  &:hover { text-decoration: none; color: ${(p) => (p.$primary ? "#fff" : tokens.colors.text)}; }
  &:disabled { opacity: 0.7; cursor: default; }
`;

const ActionLink = styled.a`
  ${actionStyles}
`;

const ActionButton = styled.button`
  ${actionStyles}
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 13px 14px;
  border-radius: ${tokens.radii.xl}px;
  border: 1px solid ${tokens.colors.border};
  background: ${tokens.colors.surface};
  font-size: 15px;
  font-family: inherit;
  color: ${tokens.colors.text};
  &:focus { outline: none; border-color: ${tokens.colors.violet}; }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  padding: 6px 0;
  border-bottom: 1px solid ${tokens.colors.border};
  &:last-child { border-bottom: none; }
`;

const InfoKey = styled.span`
  color: ${tokens.colors.muted};
  flex-shrink: 0;
`;

const InfoVal = styled.span`
  font-weight: 600;
  text-align: right;
  word-break: break-word;
`;

function AcceptedView({ uid, bookingId, trackToken, booking, fallbackInfos }) {
  const [calEvent, setCalEvent] = useState(null);
  const [resend, setResend] = useState("idle"); // idle | sending | sent | wait | error
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  useEffect(() => {
    // Pré-rempli avec l'e-mail du chauffeur connu de Zeplan, modifiable.
    if (!emailTouched && booking && booking.driver && booking.driver.email) setEmail(booking.driver.email);
  }, [booking, emailTouched]);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

  const base = `${apiRoot}/api/v1/driverapp/track/${uid}/${trackToken}`;
  const icsUrl = `${base}/calendar.ics`;
  const trackUrl = `/track/${uid}/${trackToken}`;

  useEffect(() => {
    if (!trackToken) return;
    axios
      .get(`${icsUrl}?format=json`)
      .then((res) => setCalEvent(res.data))
      .catch(() => setCalEvent(null));
  }, [icsUrl, trackToken]);

  const handleResend = () => {
    if (resend === "sending") return;
    setResend("sending");
    axios
      .post(`${base}/resend-bon`, { email: email.trim() })
      .then(() => setResend("sent"))
      .catch((error) => {
        const status = error.response && error.response.status;
        setResend(status === 429 ? "wait" : "error");
      });
  };

  const m = pickupMoment(booking);
  const timeLabel = m ? m.format("HH:mm") : null;
  const dateLabel = m ? m.locale("fr").format("dddd DD MMM YYYY") : null;
  const fallback = splitPickupDate(fallbackInfos ? fallbackInfos.pickup_date : "");
  const b = booking || {};
  const pickup = (b.pickup && b.pickup.address) || (fallbackInfos && fallbackInfos.pickup_address) || "";
  const dropoff = (b.dropoff && b.dropoff.address) || (fallbackInfos && fallbackInfos.dropoff_address) || "";
  const pickupFull = (b.pickup && b.pickup.full_address) || pickup;
  const dropoffFull = (b.dropoff && b.dropoff.full_address) || dropoff;
  // Même contenu et même ordre que le mail « bon de mission » chauffeur.
  const bonRows = [
    ["Course No.", bookingId],
    ["Name", `${b.firstname || ""} ${b.lastname || ""}`.trim()],
    ["Passengers", b.passengers != null ? String(b.passengers) : ""],
    ["Bags", b.bags != null ? String(b.bags) : ""],
    ["Vehicle", b.vehicle || (fallbackInfos && fallbackInfos.vehicle) || ""],
    ["Trip Type", b.type || ""],
    ["Pickup Time", m ? m.format("DD/MM/YYYY [at] HH:mm") : [fallback.date, fallback.time].filter(Boolean).join(" at ")],
    ["Pickup Address", pickup],
    ["Full Pickup Address", pickupFull],
    ...(b.step_address ? [["Stop", b.step_address]] : []),
    ["Dropoff Address", dropoff],
    ["Full Dropoff Address", dropoffFull],
    ["Informations", b.additional_info || ""],
    ["Flight Number", b.flight_number || ""],
    ["Phone Number", !b.hide_phone ? b.phone || "" : ""],
    ["Company", (b.driver && b.driver.company && b.driver.company.name) || ""],
    ["Driver", (b.driver && b.driver.name) || ""],
    ["Driver Price", formatPrice(b.driver_price !== undefined ? b.driver_price : fallbackInfos && fallbackInfos.driver_price) || ""],
  ];

  return (
    <PageBg>
      <Column>
        <Hero>
          <HeroMission>Mission {bookingId} · acceptée ✓</HeroMission>
          <HeroLabel>Départ prévu à</HeroLabel>
          <HeroTime>{timeLabel || fallback.time || "—"}</HeroTime>
          <HeroDate>{dateLabel || fallback.date || " "}</HeroDate>
        </Hero>

        <Scroll>
          <CardsStack>
            <SectionLabel>Bon de mission</SectionLabel>
            <Card>
              {bonRows.map(([label, value]) => (
                <InfoRow key={label}>
                  <InfoKey>{label}</InfoKey>
                  <InfoVal style={{ whiteSpace: "pre-wrap" }}>
                    {label === "Phone Number" && value ? <a href={`tel:${value}`}>{value}</a> : value || "—"}
                  </InfoVal>
                </InfoRow>
              ))}
            </Card>

            <SectionLabel>Votre course</SectionLabel>

            {trackToken ? (
              <>
                <ActionLink href={icsUrl} $primary>
                  <Icon path={ICON_PATHS.calendar} size={18} sw={2.2} />
                  Ajouter à mon agenda
                </ActionLink>
                {calEvent && (
                  <ActionLink href={googleCalendarUrl(calEvent)} target="_blank" rel="noopener noreferrer">
                    Ouvrir dans Google Agenda
                  </ActionLink>
                )}
                <ActionLink href={trackUrl}>
                  <Icon path={ICON_PATHS.car} size={18} sw={2} />
                  Lien de suivi
                </ActionLink>
                <SelectFieldLabel htmlFor="confirmation-email" style={{ marginTop: 8 }}>
                  Recevoir le bon par e-mail
                </SelectFieldLabel>
                <EmailInput
                  id="confirmation-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="votre@email.fr"
                  value={email}
                  onChange={(e) => {
                    setEmailTouched(true);
                    setEmail(e.target.value);
                    if (resend !== "idle") setResend("idle");
                  }}
                />
                <ActionButton
                  type="button"
                  onClick={handleResend}
                  disabled={!emailValid || resend === "sending" || resend === "sent"}
                >
                  {resend === "sent"
                    ? "Bon envoyé par e-mail ✓"
                    : resend === "sending"
                    ? "Envoi…"
                    : resend === "wait"
                    ? "Bon déjà envoyé il y a moins de 10 min"
                    : resend === "error"
                    ? "Échec de l'envoi — réessayez"
                    : "Envoyer le bon à cette adresse"}
                </ActionButton>
              </>
            ) : (
              <p style={{ fontSize: 14, color: tokens.colors.muted }}>
                Nous venons de transmettre le bon de commande au chauffeur sélectionné.
              </p>
            )}

            <LegalMention>
              Le bon de mission vous est aussi envoyé par WhatsApp et par e-mail. Cette page reste
              accessible depuis ce navigateur.
            </LegalMention>
          </CardsStack>
        </Scroll>
      </Column>
    </PageBg>
  );
}

function Confirmation() {
  const { customer_id, booking_id, uid } = useParams();
  const [isValid, setIsValid] = useState(false);
  const [bookingInfos, setBookingInfos] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [takenByOther, setTakenByOther] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [company, setCompany] = useState(null);
  const [driver, setDriver] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [trackToken, setTrackToken] = useState("");
  const [acceptedBooking, setAcceptedBooking] = useState(null);

  // Charge la réservation avec le jeton de suivi chauffeur (page « bon de mission »).
  const loadAccepted = (token) =>
    axios
      .get(`${apiRoot}/api/v1/driverapp/track/${uid}/${token}`)
      .then((res) => {
        setAcceptedBooking(res.data || null);
        setTrackToken(token);
        setBookingConfirmed(true);
        return true;
      })
      .catch(() => false);
  const companySelect = useRef();
  const driverSelect = useRef();

  // On iOS Safari, react-select v3 does not always focus the internal input
  // when the menu opens via tap — so the keyboard never appears and the user
  // is stuck picking from the list instead of typing. Force focus on the
  // ref after the menu opens to bring the keyboard up reliably.
  const forceFocus = (ref) => () => {
    const node = ref.current;
    if (!node) return;
    setTimeout(() => {
      if (typeof node.focus === "function") node.focus();
    }, 0);
  };

  // Hide the Voiceflow chat bubble on this screen — it overlaps the sticky
  // "J'accepte" CTA at the bottom. Same pattern as the driver tracking page.
  useEffect(() => {
    const overlay = document.getElementById("VG_OVERLAY_CONTAINER");
    if (overlay) overlay.style.display = "none";
    return () => {
      if (overlay) overlay.style.display = "block";
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);

    axios
      .get(
        `${apiRoot}/api/v1/confirmation/is-valid?uid=${uid}&md5=${customer_id}&bookingId=${booking_id}`
      )
      .then((res) => {
        setIsValid(res.data);
        if (res.data) {
          axios
            .get(
              `${apiRoot}/api/v1/confirmation/booking-infos?uid=${uid}&md5=${customer_id}&bookingId=${booking_id}`
            )
            .then((bookingRes) => {
              setBookingInfos(bookingRes.data);
              setIsLoading(false);
            })
            .catch(() => setIsLoading(false));
        } else {
          // Course déjà prise : si c'est CE navigateur qui l'a acceptée, on
          // retrouve le bon via le jeton gardé en local ; sinon « plus disponible ».
          const stored = readStoredToken(uid, booking_id);
          if (stored) {
            loadAccepted(stored).finally(() => setIsLoading(false));
          } else {
            setIsLoading(false);
          }
        }
      })
      .catch(() => setIsLoading(false));

    axios
      .get(`${apiRoot}/api/v1/confirmation/companies?uid=${uid}`)
      .then((res) => {
        setCompanyOptions(
          res.data.map((comp) => ({ label: comp.name, value: comp.id }))
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking_id, customer_id, uid]);

  const handleCompanyChange = (option) => {
    setCompany(option || null);
    if (driverSelect.current && driverSelect.current.clearValue) {
      driverSelect.current.clearValue();
    }
    setDriver(null);
    setDriverOptions([]);
    if (option && option.value) {
      axios
        .get(
          `${apiRoot}/api/v1/confirmation/companies/${option.value}/drivers?uid=${uid}`
        )
        .then((res) => {
          setDriverOptions(
            res.data.map((d) => ({ value: d.id, label: d.name }))
          );
        });
    }
  };

  const handleDriverChange = (option) => {
    setDriver(option || null);
  };

  const handleAccept = () => {
    if (!driver || !driver.value || isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage(null);

    axios
      .get(
        `${apiRoot}/api/v1/confirmation/selectdriver/?uid=${uid}&md5=${customer_id}&bookingId=${booking_id}&driverId=${driver.value}`
      )
      .then((res) => {
        setIsSubmitting(false);
        const token = res.data && res.data.track_token ? res.data.track_token : "";
        if (res.data) {
          setBookingConfirmed(true);
          if (token) {
            storeToken(uid, booking_id, token);
            setTrackToken(token);
            loadAccepted(token);
          }
        } else {
          setIsValid(false);
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        const status = error.response && error.response.status;
        if (status === 409 || status === 400) {
          setTakenByOther(true);
          setIsValid(false);
        } else {
          setErrorMessage(
            "Une erreur est survenue lors de la confirmation de la course. Merci de réessayer ou de contacter l'exploitant."
          );
        }
      });
  };

  const { date: pickupDateLabel, time: pickupTimeLabel } = useMemo(
    () => splitPickupDate(bookingInfos ? bookingInfos.pickup_date : ""),
    [bookingInfos]
  );

  const priceFormatted = useMemo(
    () => formatPrice(bookingInfos ? bookingInfos.driver_price : null),
    [bookingInfos]
  );

  if (isLoading) {
    return (
      <PageBg>
        <Column>
          <CenteredState>
            <div style={{ fontSize: 14, color: tokens.colors.muted }}>
              Chargement…
            </div>
          </CenteredState>
        </Column>
      </PageBg>
    );
  }

  if (bookingConfirmed) {
    return (
      <AcceptedView
        uid={uid}
        bookingId={booking_id}
        trackToken={trackToken}
        booking={acceptedBooking}
        fallbackInfos={bookingInfos}
      />
    );
  }

  if (!isValid) {
    return (
      <PageBg>
        <Column>
          <CenteredState>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
              {takenByOther
                ? "Cette course vient d'être acceptée par un autre chauffeur et n'est plus disponible."
                : "Cette course n'est plus disponible"}
            </h2>
          </CenteredState>
        </Column>
      </PageBg>
    );
  }

  const ctaDisabled = !company || !driver || isSubmitting;
  const hasPrice = priceFormatted !== null;

  return (
    <PageBg>
      <Column>
        <Hero>
          <HeroMission>Mission {booking_id}</HeroMission>
          <HeroLabel>Départ prévu à</HeroLabel>
          <HeroTime>{pickupTimeLabel || "—"}</HeroTime>
          <HeroDate>{pickupDateLabel || " "}</HeroDate>
        </Hero>

        <Scroll>
          <CardsStack>
            {bookingInfos && (
              <>
                <Card>
                  <TripRow>
                    <TimelineCol>
                      <TimelineDot />
                      <TimelineLine />
                      <TimelineDiamond />
                    </TimelineCol>
                    <TripAddresses>
                      <div>{bookingInfos.pickup_address}</div>
                      {bookingInfos.step_address && (
                        <div style={{ color: tokens.colors.muted }}>
                          Arrêt : {bookingInfos.step_address}
                        </div>
                      )}
                      <div>{bookingInfos.dropoff_address}</div>
                    </TripAddresses>
                  </TripRow>
                </Card>

                {bookingInfos.vehicle && (
                  <Card>
                    <VehicleRow>
                      <IconBadge>
                        <Icon path={ICON_PATHS.car} size={20} sw={2} />
                      </IconBadge>
                      <div>
                        <VehicleLabel>Véhicule</VehicleLabel>
                        <VehicleValue>{bookingInfos.vehicle}</VehicleValue>
                      </div>
                    </VehicleRow>
                  </Card>
                )}
              </>
            )}

            <SectionLabel>Affectation</SectionLabel>

            <SelectGroup>
              <SelectFieldLabel htmlFor="confirmation-company">
                Société
              </SelectFieldLabel>
              <Select
                ref={companySelect}
                inputId="confirmation-company"
                options={companyOptions}
                value={company}
                onChange={handleCompanyChange}
                placeholder="Tapez pour rechercher une société…"
                isSearchable
                blurInputOnSelect
                onMenuOpen={forceFocus(companySelect)}
                noOptionsMessage={() => "Aucune société correspondante"}
                styles={selectStyles}
                components={{ Control: CompanyControl, DropdownIndicator }}
              />
            </SelectGroup>

            <SelectGroup>
              <SelectFieldLabel htmlFor="confirmation-driver">
                Chauffeur
              </SelectFieldLabel>
              <Select
                ref={driverSelect}
                inputId="confirmation-driver"
                options={driverOptions}
                value={driver}
                onChange={handleDriverChange}
                placeholder={
                  company
                    ? "Tapez pour rechercher un chauffeur…"
                    : "Sélectionnez d'abord une société"
                }
                isDisabled={!company}
                isSearchable
                blurInputOnSelect
                onMenuOpen={forceFocus(driverSelect)}
                noOptionsMessage={() =>
                  company
                    ? "Aucun chauffeur correspondant"
                    : "Sélectionnez d'abord une société"
                }
                styles={selectStyles}
                components={{ Control: DriverControl, DropdownIndicator }}
              />
            </SelectGroup>

            <LegalMention>
              En acceptant cette mission, j'atteste être en règle avec la
              réglementation VTC (permis, carte pro, assurance de transports à
              titre onéreux).
            </LegalMention>

            {errorMessage && (
              <div
                role="alert"
                style={{
                  background: "#FEE2E2",
                  color: "#991B1B",
                  padding: "10px 12px",
                  borderRadius: tokens.radii.lg,
                  fontSize: 13,
                }}
              >
                {errorMessage}
              </div>
            )}
          </CardsStack>
        </Scroll>

        <StickyFooter>
          <FooterRow>
            {hasPrice && (
              <PriceBlock>
                <PriceLabel>Prix course</PriceLabel>
                <PriceValue>{priceFormatted}</PriceValue>
              </PriceBlock>
            )}
            <AcceptButton
              type="button"
              onClick={handleAccept}
              $disabled={ctaDisabled}
              $fullWidth={!hasPrice}
              disabled={ctaDisabled}
            >
              {isSubmitting ? (
                <>
                  <Icon path={ICON_PATHS.check} size={18} sw={2.2} />
                  Acceptation…
                </>
              ) : (
                <>
                  <Icon path={ICON_PATHS.check} size={18} sw={2.2} />
                  J'accepte
                </>
              )}
            </AcceptButton>
          </FooterRow>
        </StickyFooter>
      </Column>
    </PageBg>
  );
}

export default Confirmation;
